'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { useBirhausThemeV3 } from '@birhaus/themes'
import { 
  Settings, 
  Eye, 
  Ruler, 
  Palette, 
  Zap, 
  ChevronDown,
  ChevronRight,
  Code,
  Monitor,
  Smartphone,
  Tablet,
  RefreshCw
} from 'lucide-react'

// Local cn utility
const cn = (...inputs: (string | boolean | undefined | null)[]) => twMerge(clsx(inputs))

// V3 Dev Tools Panel variants with glass morphism
const devToolsPanelV3Variants = cva(
  [
    'fixed z-50 transition-all duration-300 ease-smooth',
    'backdrop-blur-md border border-white/30 shadow-2xl overflow-hidden'
  ],
  {
    variants: {
      position: {
        topRight: 'top-4 right-4',
        topLeft: 'top-4 left-4',
        bottomRight: 'bottom-4 right-4',
        bottomLeft: 'bottom-4 left-4',
        floating: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
      },
      size: {
        compact: 'w-80',
        standard: 'w-96',
        expanded: 'w-[32rem]',
        fullPanel: 'w-[40rem] h-[32rem]'
      },
      variant: {
        panel: [
          'bg-white/80 backdrop-saturate-180 border-white/50',
          'rounded-3xl'
        ],
        floating: [
          'bg-white/70 backdrop-saturate-150 border-white/40',
          'rounded-[2rem] shadow-xl'
        ],
        minimal: [
          'bg-white/60 backdrop-saturate-120 border-white/30',
          'rounded-2xl'
        ]
      }
    },
    defaultVariants: {
      position: 'topRight',
      size: 'standard',
      variant: 'panel'
    }
  }
)

// Tool section variants
const toolSectionV3Variants = cva(
  [
    'transition-all duration-200 ease-smooth',
    'rounded-2xl border border-white/20'
  ],
  {
    variants: {
      expanded: {
        true: 'bg-white/40 backdrop-blur-sm p-4',
        false: 'bg-transparent hover:bg-white/20 p-2'
      }
    },
    defaultVariants: {
      expanded: false
    }
  }
)

export interface DevicePreview {
  name: string
  width: number
  height: number
  icon: React.ComponentType<any>
}

export interface BirhausDevToolsV3Props extends 
  VariantProps<typeof devToolsPanelV3Variants> {
  
  // Visibility
  open?: boolean
  onToggle?: () => void
  
  // Tools configuration
  enableCognitiveLoad?: boolean
  enableSpacingGuides?: boolean
  enableColorPalette?: boolean
  enablePerformanceMonitor?: boolean
  enableDevicePreview?: boolean
  enableAccessibilityCheck?: boolean
  
  // V3 Glass styling
  enableGlass?: boolean
  reducedMotion?: boolean
  
  // Customization
  title?: string
  titleEs?: string
  titleEn?: string
  
  // Device preview options
  devices?: DevicePreview[]
  currentDevice?: string
  onDeviceChange?: (device: DevicePreview) => void
  
  // Callbacks
  onCognitiveLoadToggle?: (enabled: boolean) => void
  onSpacingGuidesToggle?: (enabled: boolean) => void
  onColorPaletteToggle?: (enabled: boolean) => void
  onPerformanceToggle?: (enabled: boolean) => void
  onAccessibilityToggle?: (enabled: boolean) => void
  onRefreshPage?: () => void
  
  // Theme
  themeName?: 'financial' | 'healthcare' | 'education'
  
  // Container props
  className?: string
  contentClassName?: string
}

/**
 * BirhausDevToolsV3 - Glass morphism developer tools panel for BIRHAUS v3.0
 * 
 * Features:
 * - Glass morphism floating developer panel
 * - Cognitive load meter integration
 * - Spacing and layout guides overlay
 * - Color palette and theme inspector
 * - Performance monitoring dashboard
 * - Device preview and responsive testing
 * - Accessibility compliance checker
 * - Spanish-first developer interface
 * - Generous spacing and radical border radius
 * - Collapsible tool sections with smooth animations
 */
export function BirhausDevToolsV3({
  open = false,
  onToggle,
  position = 'topRight',
  size = 'standard',
  variant = 'panel',
  enableCognitiveLoad = true,
  enableSpacingGuides = true,
  enableColorPalette = true,
  enablePerformanceMonitor = true,
  enableDevicePreview = true,
  enableAccessibilityCheck = true,
  enableGlass = true,
  reducedMotion = false,
  title,
  titleEs,
  titleEn,
  devices = [
    { name: 'Desktop', width: 1920, height: 1080, icon: Monitor },
    { name: 'Tablet', width: 768, height: 1024, icon: Tablet },
    { name: 'Mobile', width: 375, height: 667, icon: Smartphone }
  ],
  currentDevice,
  onDeviceChange,
  onCognitiveLoadToggle,
  onSpacingGuidesToggle,
  onColorPaletteToggle,
  onPerformanceToggle,
  onAccessibilityToggle,
  onRefreshPage,
  themeName = 'financial',
  className,
  contentClassName,
  ...props
}: BirhausDevToolsV3Props) {
  const { language } = useBirhaus()
  const theme = useBirhausThemeV3(themeName)
  
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set())
  const [cognitiveLoadEnabled, setCognitiveLoadEnabled] = React.useState(false)
  const [spacingGuidesEnabled, setSpacingGuidesEnabled] = React.useState(false)
  const [colorPaletteEnabled, setColorPaletteEnabled] = React.useState(false)
  const [performanceEnabled, setPerformanceEnabled] = React.useState(false)
  const [accessibilityEnabled, setAccessibilityEnabled] = React.useState(false)

  // Toggle section expansion
  const toggleSection = React.useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }, [])

  // Handle tool toggles
  const handleCognitiveLoadToggle = React.useCallback(() => {
    const newState = !cognitiveLoadEnabled
    setCognitiveLoadEnabled(newState)
    onCognitiveLoadToggle?.(newState)
  }, [cognitiveLoadEnabled, onCognitiveLoadToggle])

  const handleSpacingGuidesToggle = React.useCallback(() => {
    const newState = !spacingGuidesEnabled
    setSpacingGuidesEnabled(newState)
    onSpacingGuidesToggle?.(newState)
  }, [spacingGuidesEnabled, onSpacingGuidesToggle])

  const handleColorPaletteToggle = React.useCallback(() => {
    const newState = !colorPaletteEnabled
    setColorPaletteEnabled(newState)
    onColorPaletteToggle?.(newState)
  }, [colorPaletteEnabled, onColorPaletteToggle])

  const handlePerformanceToggle = React.useCallback(() => {
    const newState = !performanceEnabled
    setPerformanceEnabled(newState)
    onPerformanceToggle?.(newState)
  }, [performanceEnabled, onPerformanceToggle])

  const handleAccessibilityToggle = React.useCallback(() => {
    const newState = !accessibilityEnabled
    setAccessibilityEnabled(newState)
    onAccessibilityToggle?.(newState)
  }, [accessibilityEnabled, onAccessibilityToggle])

  // Get current texts based on language
  const currentTitle = React.useMemo(() => {
    if (title) return title
    return language === 'es' ? 
      (titleEs || 'Herramientas de Desarrollo v3.0') : 
      (titleEn || 'Development Tools v3.0')
  }, [title, titleEs, titleEn, language])

  // Tool sections configuration
  const toolSections = React.useMemo(() => [
    {
      id: 'cognitive-load',
      icon: Eye,
      titleEs: 'Carga Cognitiva',
      titleEn: 'Cognitive Load',
      descriptionEs: 'Analiza complejidad UI según Miller\'s Law',
      descriptionEn: 'Analyze UI complexity per Miller\'s Law',
      enabled: cognitiveLoadEnabled,
      onToggle: handleCognitiveLoadToggle,
      available: enableCognitiveLoad
    },
    {
      id: 'spacing-guides',
      icon: Ruler,
      titleEs: 'Guías de Espaciado',
      titleEn: 'Spacing Guides',
      descriptionEs: 'Visualiza espaciado v3.0 generoso',
      descriptionEn: 'Visualize v3.0 generous spacing',
      enabled: spacingGuidesEnabled,
      onToggle: handleSpacingGuidesToggle,
      available: enableSpacingGuides
    },
    {
      id: 'color-palette',
      icon: Palette,
      titleEs: 'Paleta de Colores',
      titleEn: 'Color Palette',
      descriptionEs: 'Inspector de temas y colores',
      descriptionEn: 'Theme and color inspector',
      enabled: colorPaletteEnabled,
      onToggle: handleColorPaletteToggle,
      available: enableColorPalette
    },
    {
      id: 'performance',
      icon: Zap,
      titleEs: 'Rendimiento',
      titleEn: 'Performance',
      descriptionEs: 'Monitor de métricas de rendimiento',
      descriptionEn: 'Performance metrics monitor',
      enabled: performanceEnabled,
      onToggle: handlePerformanceToggle,
      available: enablePerformanceMonitor
    }
  ], [
    cognitiveLoadEnabled,
    spacingGuidesEnabled,
    colorPaletteEnabled,
    performanceEnabled,
    handleCognitiveLoadToggle,
    handleSpacingGuidesToggle,
    handleColorPaletteToggle,
    handlePerformanceToggle,
    enableCognitiveLoad,
    enableSpacingGuides,
    enableColorPalette,
    enablePerformanceMonitor,
    language
  ])

  if (!open) {
    return (
      <button
        onClick={onToggle}
        className={cn(
          'fixed z-50 p-3 rounded-2xl transition-all duration-300',
          'bg-white/60 backdrop-blur-md border border-white/30',
          'hover:bg-white/80 hover:scale-105 shadow-lg',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/30',
          position === 'topRight' && 'top-4 right-4',
          position === 'topLeft' && 'top-4 left-4',
          position === 'bottomRight' && 'bottom-4 right-4',
          position === 'bottomLeft' && 'bottom-4 left-4'
        )}
        aria-label={language === 'es' ? 'Abrir herramientas de desarrollo' : 'Open development tools'}
      >
        <Settings size={20} className="text-gray-700" />
      </button>
    )
  }

  return (
    <div
      className={cn(
        devToolsPanelV3Variants({
          position,
          size,
          variant
        }),
        className
      )}
      {...props}
    >
      {/* Glass gradient overlay */}
      {enableGlass && (
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
      )}

      {/* Content */}
      <div className={cn('relative z-10 p-6 space-y-6', contentClassName)}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-100/60 backdrop-blur-sm">
              <Code size={16} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">
                {currentTitle}
              </h3>
              <p className="text-xs text-gray-600">
                BIRHAUS {language === 'es' ? 'Herramientas' : 'Tools'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={onRefreshPage}
              className={cn(
                'p-2 rounded-xl hover:bg-white/40 transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-gray-500/30'
              )}
              aria-label={language === 'es' ? 'Refrescar página' : 'Refresh page'}
            >
              <RefreshCw size={14} className="text-gray-600" />
            </button>
            
            <button
              onClick={onToggle}
              className={cn(
                'p-2 rounded-xl hover:bg-white/40 transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-gray-500/30'
              )}
              aria-label={language === 'es' ? 'Cerrar panel' : 'Close panel'}
            >
              <ChevronRight size={14} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Device Preview */}
        {enableDevicePreview && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-800">
              {language === 'es' ? 'Vista de Dispositivo' : 'Device Preview'}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              {devices.map((device) => {
                const DeviceIcon = device.icon
                const isActive = currentDevice === device.name
                
                return (
                  <button
                    key={device.name}
                    onClick={() => onDeviceChange?.(device)}
                    className={cn(
                      'p-3 rounded-xl transition-all duration-200',
                      'flex flex-col items-center gap-2 text-xs',
                      isActive ? 
                        'bg-blue-100/80 text-blue-700 ring-2 ring-blue-500/30' :
                        'bg-white/40 text-gray-600 hover:bg-white/60'
                    )}
                  >
                    <DeviceIcon size={16} />
                    <span className="font-medium">{device.name}</span>
                    <span className="text-[10px] opacity-75">
                      {device.width}×{device.height}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Tool Sections */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-800">
            {language === 'es' ? 'Herramientas Activas' : 'Active Tools'}
          </h4>
          
          <div className="space-y-2">
            {toolSections.filter(section => section.available).map((section) => {
              const SectionIcon = section.icon
              const isExpanded = expandedSections.has(section.id)
              
              return (
                <div
                  key={section.id}
                  className={cn(
                    toolSectionV3Variants({
                      expanded: isExpanded
                    })
                  )}
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        'p-2 rounded-lg backdrop-blur-sm',
                        section.enabled ? 'bg-green-100/60' : 'bg-gray-100/60'
                      )}>
                        <SectionIcon 
                          size={14} 
                          className={section.enabled ? 'text-green-600' : 'text-gray-600'} 
                        />
                      </div>
                      
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">
                          {language === 'es' ? section.titleEs : section.titleEn}
                        </div>
                        <div className="text-xs text-gray-600">
                          {language === 'es' ? section.descriptionEs : section.descriptionEn}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Enable/Disable Toggle */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          section.onToggle()
                        }}
                        className={cn(
                          'w-10 h-6 rounded-full transition-all duration-200',
                          'focus:outline-none focus:ring-2 focus:ring-blue-500/30',
                          section.enabled ? 'bg-green-500' : 'bg-gray-300'
                        )}
                      >
                        <div className={cn(
                          'w-4 h-4 rounded-full bg-white transition-transform duration-200',
                          'shadow-sm',
                          section.enabled ? 'translate-x-5' : 'translate-x-1'
                        )} />
                      </button>
                      
                      {/* Expand/Collapse */}
                      {isExpanded ? (
                        <ChevronDown size={14} className="text-gray-500" />
                      ) : (
                        <ChevronRight size={14} className="text-gray-500" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="text-xs text-gray-600">
                        {language === 'es' ? 
                          'Configuración avanzada disponible en próxima versión' :
                          'Advanced configuration available in next version'
                        }
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="p-4 rounded-2xl bg-blue-50/60 backdrop-blur-sm border border-blue-200/40">
          <div className="text-xs font-medium text-blue-900 mb-2">
            {language === 'es' ? 'Estado v3.0' : 'v3.0 Status'}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-blue-700">
                {language === 'es' ? 'Glass Morphism:' : 'Glass Morphism:'}
              </span>
              <span className="ml-1 text-green-600 font-medium">✓</span>
            </div>
            <div>
              <span className="text-blue-700">
                {language === 'es' ? 'Espaciado v3:' : 'v3 Spacing:'}
              </span>
              <span className="ml-1 text-green-600 font-medium">✓</span>
            </div>
            <div>
              <span className="text-blue-700">
                {language === 'es' ? 'Bordes Radicales:' : 'Radical Borders:'}
              </span>
              <span className="ml-1 text-green-600 font-medium">✓</span>
            </div>
            <div>
              <span className="text-blue-700">
                {language === 'es' ? 'Español Primero:' : 'Spanish First:'}
              </span>
              <span className="ml-1 text-green-600 font-medium">✓</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}